import { Check } from "lucide-react";

import {
  ColocaStatusPill,
  type ColocaStatusPillProps,
} from "@/shared/components/data-display/ColocaStatusPill";
import useTranslations from "@/shared/lang/useTranslations";

import type { KycStatus } from "../types/clients-api.types";

const VARIANT_BY_STATUS: Record<KycStatus, NonNullable<ColocaStatusPillProps["variant"]>> = {
  PENDING: "warning",
  IN_REVIEW: "info",
  VERIFIED: "success",
  REJECTED: "danger",
  EXPIRED: "neutral",
};

export interface KycStatusPillProps {
  status: KycStatus | null | undefined;
  size?: ColocaStatusPillProps["size"];
}

/**
 * KYC lifecycle pill with the module's semantic color map. Renders a muted em dash when the status is not
 * present in the payload (the QA list endpoint does not expose it yet — see docs/clients-view-gaps.md).
 */
export function KycStatusPill({ status, size }: KycStatusPillProps) {
  const { t } = useTranslations("clients");

  if (!status) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const labelByStatus: Record<KycStatus, string> = {
    PENDING: t("kyc_pending"),
    IN_REVIEW: t("kyc_in_review"),
    VERIFIED: t("kyc_verified"),
    REJECTED: t("kyc_rejected"),
    EXPIRED: t("kyc_expired"),
  };

  return (
    <ColocaStatusPill
      variant={VARIANT_BY_STATUS[status]}
      size={size}
      icon={status === "VERIFIED" ? Check : undefined}
    >
      {labelByStatus[status]}
    </ColocaStatusPill>
  );
}
